/* Useful preset rules for validation */

import moment from 'moment';
import validator from 'validator';

import { getParsedPhoneNumber } from 'pages/checkout/utils/phoneUtils';

import { ValidationRuleDefinition } from './Validator';

export enum ValidationRulePresetName {
  email = 'email',
  password = 'password',
  phone = 'phone',
  phoneCountryCode = 'phoneCountryCode',
  required = 'required',
  insurance = 'insurance',
  isTrue = 'isTrue',
  dateOfBirth = 'dateOfBirth',
}

const phoneValidator = (value: string) => {
  const parsedPhoneNumber = getParsedPhoneNumber(value);
  return Boolean(parsedPhoneNumber?.isValid());
};

const phoneCountryCodeValidator = (value: string) => {
  const parsedPhoneNumber = getParsedPhoneNumber(value);

  if (!parsedPhoneNumber?.isValid()) {
    return false;
  }

  return parsedPhoneNumber.countryCallingCode === '1';
};

interface DateOfBirth {
  month: string | null;
  day: string | null;
  year: string | null;
}

const MIN_YEAR = 1900;
const MIN_DATE = moment([MIN_YEAR]);
const MAX_DATE = moment();

const dateOfBirthValidator = (dateOfBirth: DateOfBirth | null | undefined): boolean => {
  if (!dateOfBirth?.month || !dateOfBirth?.day || !dateOfBirth?.year) {
    return false;
  }

  const isValidYear = dateOfBirth.year.length === 4;
  const isValidMonth = dateOfBirth.month.length === 2;
  const isValidDay = dateOfBirth.day.length === 2;

  if (!isValidYear || !isValidMonth || !isValidDay) {
    return false;
  }

  const dateMoment = moment([
    parseInt(dateOfBirth.year, 10),
    parseInt(dateOfBirth.month, 10) - 1,
    parseInt(dateOfBirth.day, 10),
  ]);

  if (!dateMoment.isValid()) {
    return false;
  }

  if (dateMoment.isBefore(MIN_DATE, 'day')) {
    return false;
  }

  if (dateMoment.isAfter(MAX_DATE, 'day')) {
    return false;
  }

  return true;
};

export const ValidationRulePreset: Record<ValidationRulePresetName, ValidationRuleDefinition> = {
  email: Object.freeze({
    validatorFunction: validator.isEmail,
    validWhen: true,
    message: 'Invalid email',
  }),
  password: Object.freeze({
    validatorFunction: validator.isLength,
    validWhen: true,
    args: [8, 100],
    message: 'Password must be at least 8 characters',
  }),
  phone: Object.freeze({
    customValidatorFunction: phoneValidator,
    validWhen: true,
    message: 'Invalid phone number',
  }),
  phoneCountryCode: Object.freeze({
    customValidatorFunction: phoneCountryCodeValidator,
    validWhen: true,
    message: 'Phone number must be from the United States or Canada',
  }),
  required: Object.freeze({
    validatorFunction: validator.isEmpty,
    validWhen: false,
    message: 'Required',
  }),
  isTrue: Object.freeze({
    customValidatorFunction: (value: boolean) => value,
    validWhen: true,
    message: 'Required',
  }),
  insurance: Object.freeze({
    customValidatorFunction: (value: { id: number; name: string; }) => {
      return Boolean(value?.id && value?.name);
    },
    validWhen: true,
    message: 'Required',
  }),
  dateOfBirth: Object.freeze({
    customValidatorFunction: dateOfBirthValidator,
    validWhen: true,
    message: 'Please enter a valid date',
  }),
};
